import React from 'react';

import parse from 'html-react-parser';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { BonusRedeemType } from '@lucky7ventures/bff-types';

import CreditButton from '@/components/shared/bonus-card/CreditButton';
import CashierButton from '@/components/shared/bonus-card/CashierButton';
import ForfeitButton from '@/components/shared/bonus-card/ForfeitButton';
import { isEmpty } from '@/shared/utility';
import { openModal } from '@/store/actions/modal';
import { cdnBonusImg } from '@/utils/image-utils';

interface BonusCardProps {
  // Props for both available and active bonuses
  id: string;
  desc: string;
  name: string;
  bonusCode: string;
  redeemTypeId: BonusRedeemType;
  availableUntilText: string;
  bonusTerms: string;
  shortTerms: string;
  image: string;
  bonusType: string;
  // Props for active bonuses
  userBonusId?: number;
  bonusMoney?: number;
  realBonusMoney?: number;
  // General props
  className?: string;
}

function BonusCard({
  bonusCode,
  id,
  name,
  image,
  desc,
  bonusType,
  redeemTypeId,
  bonusTerms,
  availableUntilText,
  userBonusId,
  shortTerms,
  realBonusMoney,
  bonusMoney,
  className,
}: BonusCardProps) {
  const dispatch = useDispatch();
  const intl = useIntl();

  return (
    <>
      <div
        className={classNames(
          `rounded-md bg-white m-4 relative overflow-hidden max-w-[350px] min-w-[250px] w-full
          pt-12 px-8 pb-4 flex flex-col flex-wrap justify-start flex-1 sm:-mt-16 sm:mb-[80px]`,
          className,
        )}
        data-cy="bonus-card"
      >
        <div className="absolute -top-[20px] left-0 sm:-top-[100px]" id={id} />
        <div className="relative flex justify-between text-center flex-col flex-wrap flex-1">
          <div className="text">
            <p className="text-emerald-600 font-bold text-sm rounded-[50vh] inline-block justify-end mb-4">
              {bonusType}
            </p>
            <h3 className="mt-0 text-gray-900 text-[22px] mb-4">{name}</h3>
            <div className="mb-0 italic font-bold opacity-80">
              {!isEmpty(desc) && <p className="description">{desc}</p>}
              {!isEmpty(availableUntilText) && (
                <p className="bonusExpiration">
                  {intl.formatMessage({ id: 'bonusCard.availableUntil' })}: <br />
                  {availableUntilText}
                </p>
              )}
            </div>
            {!isEmpty(image) && (
              <img
                src={cdnBonusImg(image, {
                  width: '180',
                  height: 'auto',
                  dpr: '2',
                  fit: 'contain',
                })}
                alt={image}
                className="w-full max-w-[90px] mx-auto mb-2.5 h-auto"
              />
            )}
          </div>
          <div className="flex justify-center flex-wrap mt-2.5">
            {redeemTypeId === BonusRedeemType.BonusCode && <CreditButton promoCode={bonusCode} />}
            {(redeemTypeId === BonusRedeemType.DepositBonusCode ||
              redeemTypeId === BonusRedeemType.Deposit) && <CashierButton promoCode={bonusCode} />}
            {redeemTypeId === BonusRedeemType.ForfeitBonus && (
              <ForfeitButton
                bonusMoney={bonusMoney!}
                realBonusMoney={realBonusMoney!}
                userBonusId={userBonusId!}
                bonusName={name}
              />
            )}
            <span
              className="text-black bg-none text-sm font-bold m-0 p-4 transition-opacity duration-200 ease-in-out
              block w-full no-underline opacity-60 hover:opacity-100 cursor-pointer"
              onClick={() =>
                dispatch(
                  openModal('bonusTerms', {
                    text: parse(bonusTerms.toString()),
                  }),
                )
              }
              data-cy="bonus-card-bonus-terms"
            >
              {intl.formatMessage({ id: 'bonusCard.termsLink' })}
            </span>
            {shortTerms && <p className="mt-4 text-xs italic">{shortTerms}</p>}
          </div>
        </div>
      </div>
    </>
  );
}

export default BonusCard;
